<template>
  <HeadTitle
    title="Crear cartas externas"
    description="Formulario para cargar cartas externas"
    class="mb-3"
  />
  <Step :stepNumber="1" title="Configuración basica">
    <div class="formgrid grid w-12 md:w-6">
      <div class="field col-10 md:col-6 flex flex-column">
        <label for="name">Nombre <span class="text-red-600">*</span></label>
        <InputText v-model="name" id="name" type="text" />
        <small id="text-error" class="p-error">{{
          nameError || '&nbsp;'
        }}</small>
      </div>
      <div class="field col-10 md:col-6 flex flex-column">
        <label for="description"
          >Descripción <span class="text-red-600">*</span></label
        >
        <InputText id="description" v-model="description" />
        <small id="text-error" class="p-error">{{
          descriptionError || '&nbsp;'
        }}</small>
      </div>
      <div class="field col-10 md:col-8 flex flex-column">
        <div class="flex flex-row mb-2">
          <label for="approver">Correos a notificar</label>
          <InfoButton
            message="Por cada archivo cargado se enviara un correo con copia del archivo a los emails seleccionados"
            class=""
          />
        </div>
        <Chips v-model="emailsToNotify" separator="," invalid />
        <small id="text-error" class="p-error">{{
          emailsError || '&nbsp;'
        }}</small>
      </div>
    </div>
  </Step>
  <Step :stepNumber="2" title="Carga y validación de archivos">
    <div class="flex flex-row w-6">
      <FileUpload
        mode="basic"
        accept=".zip"
        chooseLabel="cargar archivo"
        class="text-sm font-light"
        custom-upload
        @uploader="removeFile"
        @select="saveFile($event)"
      />
      <InfoButton
        message="Solo se permite subir un archivo comprimido de tipo .zip, que contenga archivos nombrados con el siguiente formato: <Codigo de nomina>_<numero de identificación>.pdf.
                    Segun el formato de los archivos, se crearan las cartas asociadas a los empleados."
        class="ml-2"
      />
    </div>
    <div class="flex flex-column w-11">
      <DataTable
        class="tabla mb-5"
        :value="mappedExternalFiles.data"
        paginator
        :rows="10"
        :rowsPerPageOptions="[5, 10, 20, 50]"
        style="width: 100%"
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        :totalRecords="mappedExternalFiles.totalItems"
        :currentPageReportTemplate="currentPageReportTemplate"
        scrollable
        scrollHeight="600px"
        :loading="fileAreUploading"
        v-model:filters="filters"
        v-model:selection="selectedRecordsToDelete"
        :globalFilterFields="['fileName', 'employee.code', 'employee.name']"
      >
        <template #empty> No hay archivos a validar.</template>
        <template #header>
          <div class="flex justify-content-end">
            <Button
              icon="pi pi-trash"
              label="Remover Seleción"
              @click="onRemoveRecords($event)"
              severity="secondary"
              class="text-sm font-light mr-3"
            />
            <ConfirmPopup></ConfirmPopup>
            <span class="p-input-icon-left">
              <i class="pi pi-search" />
              <InputText
                v-model="filters['global'].value"
                placeholder="Buscar"
              />
            </span>
          </div>
        </template>
        <Column selectionMode="multiple" headerStyle="width: 3rem"> </Column>
        <Column field="fileName" header="Nombre" sortable></Column>
        <Column field="employee" header="Empleado" sortable>
          <template #body="{ data, field }">
            <span v-if="data[field]"
              >{{ data[field].code }} - {{ data[field].name }}</span
            >
          </template>
        </Column>
        <Column field="error" header="Error" sortable>
          <template #body="{ data, field }">
            <span v-if="data[field]" class="text-red-400 text-sm">{{
              data[field]
            }}</span>
          </template>
        </Column>
        <Column header="Acciones">
          <template #body="{ data }">
            <div class="actions">
              <Button
                v-tooltip.bottom="'Ver archivo'"
                class="action-btn view p-button-sm mr-2"
                icon="pi pi-eye"
                @click="downloadFile(data.url)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </Step>
  <Footer>
    <Button
      label="Cancelar"
      class="p-button-secondary text-sm font-light mr-2"
      @click="router.back()"
    />
    <Button
      label="Guardar"
      class="p-button-primary text-sm font-light"
      :disabled="!isValidToSave"
      :loading="isSubmitting"
      @click="onSave"
    />
  </Footer>
</template>
<script setup lang="ts">
  import * as yup from 'yup'
  import Step from '@/shared/components/Step.vue'
  import Footer from '@/shared/components/Footer.vue'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import useCompany from '@/store/company'

  import useNotifications from '@/store/notification'
  import { storeToRefs } from 'pinia'
  import { useField } from 'vee-validate'
  import { computed, reactive, ref } from 'vue'
  import InfoButton from '@/shared/components/InfoButton.vue'
  import router from '@/router'
  import ExternalLetterMapped from '../../models/ExternalLetterMapped'
  import {
    createExternalLetters,
    uploadExternalLettersZip,
  } from '../../services/ExternalLetterService'
  import ICreateExternalLetterInputDto, {
    ExternalLetterInputDto,
  } from '../../services/dtos/ICreateExternalLetters'
  import { getFileUrlPresigned } from '@/shared/services/fileService'
  import { FilterMatchMode } from 'primevue/api'
  import { useConfirm } from 'primevue/useconfirm'

  const companyStore = useCompany()
  const confirm = useConfirm()
  const notificationStore = useNotifications()
  const { company } = storeToRefs(companyStore)
  const fileAreUploading = ref(false)
  const isSubmitting = ref(false)
  const selectedRecordsToDelete = ref<ExternalLetterMapped[]>([])

  const file = ref<File>()
  const mappedExternalFiles = reactive<{
    data: ExternalLetterMapped[]
    totalItems: number
  }>({
    data: [],
    totalItems: 0,
  })

  companyStore.$subscribe(async (_, state) => {
    company.value = state.company
  })

  const getExternalLetters = computed(() => {
    const externalLetters: ExternalLetterInputDto[] = []
    mappedExternalFiles.data.forEach((exFile) => {
      if (exFile.employee && exFile.url) {
        const externalLetter: ExternalLetterInputDto = {
          employeeId: exFile.employee.id,
          url: exFile.url,
        }
        externalLetters.push(externalLetter)
      }
    })
    return externalLetters
  })

  const getPayrollsIds = computed<string[]>(() => {
    const payrollsIds = new Set<string>()
    mappedExternalFiles.data.forEach((file) => {
      if (file.payrollId) payrollsIds.add(file.payrollId)
    })

    return Array.from(payrollsIds)
  })

  const saveFile = async (event: any) => {
    const file = event.files[0]
    file.value = file

    if (company.value === null) {
      notificationStore.addErrorNotification({
        detail: 'No se ha seleccionado una empresa',
        severity: 'info',
        summary: 'Favor selecciona un empresa antes de cargar los archivos',
      })
    } else {
      fileAreUploading.value = true
      const response = await uploadExternalLettersZip(file, company.value.id)
      mappedExternalFiles.data = response.data
      mappedExternalFiles.totalItems = response.totalItems
      fileAreUploading.value = false
    }
  }

  const removeFile = () => {
    file.value = undefined
  }

  const nameSchema = yup.string().required('El nombre es requerido')
  const descriptionSchema = yup.string().required('La descripción es requerida')
  const emailsSchema = yup
    .array()
    .test('emails', 'Ingresa un correo valido', (value) => {
      if (value) {
        const emails = value
        const regex = new RegExp(
          '^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$'
        )
        return emails.every((email) => regex.test(email.trim()))
      }
      return true
    })

  const currentPageReportTemplate = computed(() => {
    return `{first} a {last} de ${mappedExternalFiles.totalItems}`
  })

  const {
    value: description,
    errorMessage: descriptionError,
    validate: descValidate,
  } = useField<string>('description', descriptionSchema)
  const {
    value: name,
    errorMessage: nameError,
    validate: nameValidate,
  } = useField<string>('description', nameSchema)
  const {
    value: emailsToNotify,
    errorMessage: emailsError,
    validate: emailsValidate,
  } = useField<string[]>('emailsToNotify', emailsSchema)

  const isErrorInMappedFiles = computed(() => {
    return mappedExternalFiles.data.some((file) => file.error)
  })

  const isValidToSave = computed(() => {
    return (
      mappedExternalFiles.data.length > 0 &&
      !isErrorInMappedFiles.value &&
      name.value &&
      description.value &&
      !emailsError.value
    )
  })

  const onSave = async () => {
    if (!isValidToSave.value) {
      return
    }

    const dto: ICreateExternalLetterInputDto = {
      name: name.value,
      description: description.value,
      emailsToNotify: emailsToNotify.value,
      companyId: company.value?.id || '',
      payrollsIds: getPayrollsIds.value,
      externalLetters: getExternalLetters.value,
    }

    isSubmitting.value = true
    try {
      const response = await createExternalLetters(dto)

      if (response === 'OK') {
        notificationStore.addSuccesNotification({
          detail: 'Cartas externas creadas correctamente',
          severity: 'success',
          summary: 'Cartas externas creadas',
        })
        router.back()
      }
    } finally {
      isSubmitting.value = false
    }
  }

  const downloadFile = (urlOrKey: string) => {
    if (!urlOrKey.startsWith('http')) {
      getFileUrlPresigned(urlOrKey).then((url) => {
        window.open(url, '_blank')
      })
    } else {
      window.open(urlOrKey, '_blank')
    }
  }

  const onRemoveRecords = (event: any) => {
    confirm.require({
      target: event.currentTarget,
      message: '¿Realmente quieres eliminar los registros?',
      icon: 'pi pi-info-circle',
      acceptClass: 'p-button-danger',
      acceptLabel: 'si',
      rejectLabel: 'no',
      acceptIcon: 'pi pi-check',
      accept: async () => {
        if (selectedRecordsToDelete.value.length === 0) {
          notificationStore.addErrorNotification({
            detail: 'No hay registros seleccionados',
            severity: 'info',
            summary: 'Favor selecciona un registro para eliminar',
          })
          return
        }
        mappedExternalFiles.data = mappedExternalFiles.data.filter(
          (file) => !selectedRecordsToDelete.value.includes(file)
        )
        mappedExternalFiles.totalItems = mappedExternalFiles.data.length
        selectedRecordsToDelete.value = []
      },
      reject: () => {},
    })
  }

  const filters = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  })
</script>
